import { Card, Typography, Space, Form, Input, Button, message, Alert } from 'antd';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet, apiPut, type MeResponse } from '../api/client';

interface BillingStatus {
  planCode: string;
  status: string;
  hasPaymentMethod: boolean;
  hasActiveSubscription: boolean;
  trialEndsAt: string | null;
  currentPeriodEndsAt: string | null;
  cancelAt: string | null;
  isApiAccessAllowed: boolean;
  isComplimentary?: boolean;
}

export function Dashboard() {
  const navigate = useNavigate();
  const [me, setMe] = useState<MeResponse | null>(null);
  const [billing, setBilling] = useState<BillingStatus | null>(null);
  const [brokerage, setBrokerage] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiGet<MeResponse>('/portal/me')
      .then((data) => {
        setMe(data);
        setBrokerage(data.brokerageName ?? '');
      })
      .catch(() => {
        // ignore — Layout handles auth redirect
      });
    apiGet<BillingStatus>('/portal/billing/status')
      .then(setBilling)
      .catch(() => {
        // non-fatal — Billing page surfaces the same error if it persists
      });
  }, []);

  async function saveBrokerage() {
    setSaving(true);
    try {
      const result = await apiPut<{ brokerageName: string | null }>('/portal/me/brokerage', {
        brokerageName: brokerage.trim() || null,
      });
      setMe((prev) => (prev ? { ...prev, brokerageName: result.brokerageName } : prev));
      message.success('Brokerage name saved');
    } catch (e) {
      message.error('Failed to save: ' + (e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  // The "you can't do anything yet" state: signed up, but no card on file and
  // not on the comp allowlist. Show a high-visibility CTA driving them to /billing
  // before any other onboarding noise.
  const needsBilling = billing != null && !billing.isComplimentary && !billing.hasPaymentMethod;

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Typography.Title level={3}>Welcome</Typography.Title>

      {needsBilling && (
        <Alert
          type="warning"
          showIcon
          message="Set up billing to get started"
          description={
            <>
              You're signed up but you don't have a payment method on file yet. Connecting a CREA
              DDF feed or generating API keys requires a card — you get a <strong>7-day free
              trial</strong>, so today's charge is $0.
            </>
          }
          action={
            <Button type="primary" onClick={() => navigate('/billing')}>
              Set up billing
            </Button>
          }
        />
      )}

      {billing?.isComplimentary && (
        <Alert
          type="success"
          showIcon
          message="Complimentary access"
          description="This account has full API access with no Stripe subscription. No action needed on billing."
        />
      )}

      <Card title="Your brokerage">
        <Typography.Paragraph type="secondary">
          Shown as <em>"Listing provided courtesy of …"</em> on every listing response and in the
          per-listing trademark block (CREA's DDF terms require it on detail pages). Enter your
          brokerage's display name once — every API consumer of your data picks it up.
        </Typography.Paragraph>
        <Form layout="inline" onFinish={saveBrokerage}>
          <Form.Item label="Brokerage name" style={{ flex: 1, minWidth: 320 }}>
            <Input
              placeholder="e.g. Jump Realty Inc."
              value={brokerage}
              onChange={(e) => setBrokerage(e.target.value)}
              maxLength={256}
              allowClear
            />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={saving} disabled={!me}>
              Save
            </Button>
          </Form.Item>
        </Form>
        {me?.brokerageName && (
          <Typography.Paragraph style={{ marginTop: 12, marginBottom: 0 }}>
            Currently surfaced as: <strong>{me.brokerageName}</strong>
          </Typography.Paragraph>
        )}
      </Card>

      <Card>
        <Typography.Paragraph>
          This portal lets you connect your CREA DDF feed and generate API keys for your website
          or app. Use the side menu to:
        </Typography.Paragraph>
        <ul>
          <li>
            <strong>CREA DDF feeds</strong> — paste the Bearer token CREA gave you to start syncing.
          </li>
          <li>
            <strong>API keys</strong> — generate keys to consume the public REST API.
          </li>
          <li>
            <strong>Usage</strong> — track API call volume and sync status.
          </li>
        </ul>
        <Typography.Paragraph type="secondary">
          Listings are provided through the REALTOR.ca DDF®. The trademarks REALTOR®, REALTORS® and
          the REALTOR® logo are controlled by The Canadian Real Estate Association (CREA).
        </Typography.Paragraph>
      </Card>
    </Space>
  );
}
